import { useEffect, useState } from 'react';
import { changePassword, getUserProfile, updateUserProfile } from '../../service/userService';
import {
    createCheckoutSession,
    createPortalSession,
    fetchPrices,
    getCachedSubscriptionState,
    refreshSubscriptionState
} from '../../service/billingService';
import componentStyles from './profile.module.css';
import globalStyles from '../globals.module.css';
import eventBus from '../../service/eventBus';
import NotificationModel from '../../model/notificationModel';
import {
    EVENT_NOTIFICATION_PUSH,
    EVENT_UPDATE_ACTION_BAR_VIEW,
    EVENT_UPDATE_AUTHENTICATED,
    NOTIFICATIONS_ERROR,
    NOTIFICATIONS_SUCCESS
} from '../../utils/constants';
import SessionService from '../../service/sessionService';
import PageView from '../common/pageView';
import AnalyticsService from '../../service/analyticsService';
import { EVENT_LOGOUT, EVENT_PROFILE_UPDATED } from '../../utils/analyticsConstants';

const Profile = () => {
    const [profile, setProfile] = useState({
        username: '',
        email: '',
        currentTier: 'FREE'
    });
    const [subscriptionState, setSubscriptionState] = useState(getCachedSubscriptionState());
    const [prices, setPrices] = useState([]);
    const [isPricingLoading, setIsPricingLoading] = useState(true);
    const [pricingError, setPricingError] = useState('');
    const [checkoutLoadingKey, setCheckoutLoadingKey] = useState('');
    const [portalLoading, setPortalLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const syncAccountState = async () => {
        const [profileResponse, latestSubscription] = await Promise.all([
            getUserProfile(),
            refreshSubscriptionState()
        ]);

        if (profileResponse?.data?.success) {
            const nextProfile = profileResponse?.data?.results?.profile || {};
            setProfile((prev) => ({
                ...prev,
                ...nextProfile,
                currentTier: String(nextProfile.currentTier || prev.currentTier || 'FREE').toUpperCase()
            }));
        }

        setSubscriptionState(latestSubscription);
    };

    const pushNotification = (message, type) => {
        eventBus.emit(EVENT_NOTIFICATION_PUSH, new NotificationModel(message, type));
    };

    const loadPrices = async () => {
        setIsPricingLoading(true);
        setPricingError('');

        try {
            const nextPrices = await fetchPrices();
            setPrices(nextPrices);
        } catch (error) {
            setPricingError('Unable to load pricing right now.');
        } finally {
            setIsPricingLoading(false);
        }
    };

    useEffect(() => {
        Promise.allSettled([syncAccountState(), loadPrices()]).then((results) => {
            if (results[0]?.status === 'rejected') {
                pushNotification('Error loading account details.', NOTIFICATIONS_ERROR);
            }
        });
    }, []);

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        const trimmedUsername = String(profile.username || '').trim();
        const trimmedEmail = String(profile.email || '').trim();

        if (!/^[A-Za-z][A-Za-z0-9]{4,31}$/.test(trimmedUsername)) {
            pushNotification(
                'Username must start with a letter and be 5-32 letters or numbers.',
                NOTIFICATIONS_ERROR
            );
            return;
        }

        if (!trimmedEmail) {
            pushNotification('Email is required.', NOTIFICATIONS_ERROR);
            return;
        }

        setIsUpdating(true);

        try {
            const response = await updateUserProfile({
                username: trimmedUsername,
                email: trimmedEmail
            });
            if (response.data.success) {
                const nextProfile = response?.data?.results?.profile || {};
                AnalyticsService.getInstance().track(EVENT_PROFILE_UPDATED);
                setProfile((prev) => ({
                    ...prev,
                    ...nextProfile,
                    currentTier: String(nextProfile.currentTier || prev.currentTier || 'FREE').toUpperCase()
                }));
                await refreshSubscriptionState();
                pushNotification('Profile updated successfully!', NOTIFICATIONS_SUCCESS);
                setIsEditing(false);
            } else {
                pushNotification(response?.data?.error || 'Error updating profile.', NOTIFICATIONS_ERROR);
            }
        } catch (error) {
            pushNotification(error?.response?.data?.error || 'Error updating profile.', NOTIFICATIONS_ERROR);
        } finally {
            setIsUpdating(false);
        }
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword.length < 8) {
            eventBus.emit(
                EVENT_NOTIFICATION_PUSH,
                new NotificationModel('New password must be at least 8 characters.', NOTIFICATIONS_ERROR)
            );
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            eventBus.emit(
                EVENT_NOTIFICATION_PUSH,
                new NotificationModel('Passwords do not match.', NOTIFICATIONS_ERROR)
            );
            return;
        }

        setIsUpdating(true);

        try {
            const response = await changePassword(passwordData.currentPassword, passwordData.newPassword);
            if (response.data.success) {
                pushNotification('Password updated successfully!', NOTIFICATIONS_SUCCESS);
                setShowPasswordForm(false);
                setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
            }
        } catch (error) {
            pushNotification('Error updating password.', NOTIFICATIONS_ERROR);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCheckout = async (priceLookupKey) => {
        setCheckoutLoadingKey(priceLookupKey);
        setCheckoutError('');

        try {
            const successUrl = `${window.location.origin}/billing/checkout-result?status=success`;
            const cancelUrl = `${window.location.origin}/billing/checkout-result?status=canceled`;
            const { checkoutUrl } = await createCheckoutSession({
                priceLookupKey,
                successUrl,
                cancelUrl
            });

            if (!checkoutUrl) {
                throw new Error('Missing checkout URL');
            }

            window.location.assign(checkoutUrl);
        } catch (error) {
            setCheckoutError('Unable to start checkout right now.');
            setCheckoutLoadingKey('');
        }
    };

    const handleLogout = () => {
        AnalyticsService.getInstance().track(EVENT_LOGOUT);
        SessionService.getInstance().clearSession();
        eventBus.emit(EVENT_UPDATE_AUTHENTICATED, false);
        eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, 'snipedOdds');
    };

    const handleManageSubscription = async () => {
        setPortalLoading(true);
        setCheckoutError('');

        try {
            const { url } = await createPortalSession();

            if (!url) {
                throw new Error('Missing portal URL');
            }

            window.location.assign(url);
        } catch (error) {
            setCheckoutError('Unable to open subscription management right now.');
            setPortalLoading(false);
        }
    };

    const currentTier = String(profile?.currentTier || subscriptionState?.currentTier || 'FREE').toUpperCase();
    const subscription = subscriptionState?.subscription || {};
    const upgradePrice = prices.find((price) => {
        const isUnavailable = price.available === false;
        const isCurrentPlan = subscription.priceLookupKey === price.priceLookupKey;
        return !isUnavailable && !isCurrentPlan && /pro/i.test(price.name || '');
    }) || prices.find((price) => {
        const isUnavailable = price.available === false;
        const isCurrentPlan = subscription.priceLookupKey === price.priceLookupKey;
        return !isUnavailable && !isCurrentPlan;
    });

    return (
        <PageView
            title="Account"
            containerClassName={componentStyles.profileContainer}
            contentClassName={componentStyles.profileContent}
        >
            <section className={`${globalStyles.standardSection} ${componentStyles.profileSection}`}>
                <h3 className={globalStyles.sectionHeader}>Account Information</h3>
                {isEditing ? (
                    <form onSubmit={handleProfileUpdate} className={globalStyles.form}>
                        <div className={globalStyles.formGroup}>
                            <label>Email</label>
                            <input
                                type="email"
                                value={profile.email}
                                onChange={(e) => setProfile((prev) => ({ ...prev, email: e.target.value }))}
                                className={globalStyles.standardInput}
                                placeholder="Enter email"
                            />
                        </div>
                        <div className={globalStyles.formGroup}>
                            <label>Username</label>
                            <input
                                type="text"
                                value={profile.username}
                                onChange={(e) => setProfile((prev) => ({ ...prev, username: e.target.value }))}
                                className={globalStyles.standardInput}
                                placeholder="Enter username"
                            />
                        </div>
                        <div className={componentStyles.buttonGroup}>
                            <button type="submit" className={globalStyles.primaryButton} disabled={isUpdating}>
                                {isUpdating ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button type="button" className={globalStyles.secondaryButton} onClick={() => setIsEditing(false)}>
                                Cancel
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className={componentStyles.profileInfo}>
                        <div className={componentStyles.infoItem}>
                            <span className={componentStyles.infoLabel}>Email</span>
                            <span className={componentStyles.infoValue}>{profile.email || 'Not available'}</span>
                        </div>
                        <div className={componentStyles.infoItem}>
                            <span className={componentStyles.infoLabel}>Username</span>
                            <span className={componentStyles.infoValue}>{profile.username || 'Not set'}</span>
                        </div>
                        <button className={globalStyles.primaryButton} onClick={() => setIsEditing(true)}>
                            Edit Profile
                        </button>
                    </div>
                )}
            </section>

            <section className={`${globalStyles.standardSection} ${componentStyles.profileSection}`}>
                <h3 className={globalStyles.sectionHeader}>Password</h3>
                {showPasswordForm ? (
                    <form onSubmit={handlePasswordUpdate} className={globalStyles.form}>
                        <p className={componentStyles.passwordHelperText}>Passwords must be at least 8 characters long.</p>
                        <div className={globalStyles.formGroup}>
                            <label>Current Password</label>
                            <input
                                type="password"
                                value={passwordData.currentPassword}
                                onChange={(e) => setPasswordData((prev) => ({ ...prev, currentPassword: e.target.value }))}
                                className={globalStyles.standardInput}
                                placeholder="Enter current password"
                                required
                            />
                        </div>
                        <div className={globalStyles.formGroup}>
                            <label>New Password</label>
                            <input
                                type="password"
                                value={passwordData.newPassword}
                                onChange={(e) => setPasswordData((prev) => ({ ...prev, newPassword: e.target.value }))}
                                className={globalStyles.standardInput}
                                placeholder="Enter new password"
                                required
                            />
                        </div>
                        <div className={globalStyles.formGroup}>
                            <label>Confirm Password</label>
                            <input
                                type="password"
                                value={passwordData.confirmPassword}
                                onChange={(e) => setPasswordData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                                className={globalStyles.standardInput}
                                placeholder="Confirm new password"
                                required
                            />
                        </div>
                        <div className={componentStyles.buttonGroup}>
                            <button type="submit" className={globalStyles.primaryButton} disabled={isUpdating}>
                                {isUpdating ? 'Updating...' : 'Update Password'}
                            </button>
                            <button type="button" className={globalStyles.secondaryButton} onClick={() => setShowPasswordForm(false)}>
                                Cancel
                            </button>
                        </div>
                    </form>
                ) : (
                    <button className={globalStyles.primaryButton} onClick={() => setShowPasswordForm(true)}>
                        Change Password
                    </button>
                )}
            </section>

            <section className={`${globalStyles.standardSection} ${componentStyles.profileSection}`}>
                <h3 className={globalStyles.sectionHeader}>Subscription</h3>
                <div className={componentStyles.tierInfo}>
                    <div className={componentStyles.infoItem}>
                        <span className={componentStyles.infoLabel}>Current Tier</span>
                        <span className={componentStyles.infoValue}>{currentTier}</span>
                    </div>
                    {currentTier === 'PRO' ? (
                        <>
                            <button
                                type="button"
                                className={globalStyles.primaryButton}
                                disabled={portalLoading}
                                onClick={handleManageSubscription}
                            >
                                {portalLoading ? 'Opening Portal…' : 'Manage Subscription'}
                            </button>
                            {checkoutError && <div className={componentStyles.errorStatus}>{checkoutError}</div>}
                        </>
                    ) : (
                        <>
                            {isPricingLoading ? (
                                <button type="button" className={globalStyles.primaryButton} disabled>
                                    Loading upgrade…
                                </button>
                            ) : upgradePrice ? (
                                <button
                                    type="button"
                                    className={globalStyles.primaryButton}
                                    disabled={!!checkoutLoadingKey}
                                    onClick={() => handleCheckout(upgradePrice.priceLookupKey)}
                                >
                                    {checkoutLoadingKey === upgradePrice.priceLookupKey ? 'Starting Checkout…' : 'Upgrade to Pro'}
                                </button>
                            ) : (
                                <div className={componentStyles.errorStatus}>
                                    <span>{pricingError || 'Upgrade option not available right now.'}</span>
                                    <button type="button" className={globalStyles.secondaryButton} onClick={loadPrices}>
                                        Retry
                                    </button>
                                </div>
                            )}
                            {checkoutError && <div className={componentStyles.errorStatus}>{checkoutError}</div>}
                        </>
                    )}
                </div>
            </section>

            <button
                onClick={handleLogout}
                className={`${globalStyles.secondaryButton} ${componentStyles.dangerButton}`}
            >
                Sign Out
            </button>
        </PageView>
    );
};

export default Profile;
